(function () {
    'use strict';

    // ─── Configuration ────────────────────────────────────────────────────────
    const STOREFRONT_TOKEN = '10a0577ca9ec11181e6249841aa9a0cc';
    const STOREFRONT_URL = 'https://pingreedetroit.com/api/2024-01/graphql.json';
    const CACHE_PREFIX = 'pingree_fbt_';
    const MAX_SUGGESTIONS = 2;

    // ─── Auto-open drawer on page load if ?cart=open is in the URL ────────────
    function maybeOpenDrawer() {
        const params = new URLSearchParams(window.location.search);
        if (params.get('cart') !== 'open') return;

        // Remove the parameter from the URL cleanly without triggering a reload
        params.delete('cart');
        const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);

        // Wait for the theme to finish initializing, then open the cart drawer
        const tryOpen = (attempts) => {
            if (attempts <= 0) return;
            // Troop Themes Beyond opens the drawer by clicking the cart trigger
            const cartTrigger = document.querySelector('[data-drawer-open="right"][data-drawer-view="cart-drawer"]');
            if (cartTrigger) {
                cartTrigger.click();
            } else {
                setTimeout(() => tryOpen(attempts - 1), 300);
            }
        };

        setTimeout(() => tryOpen(5), 500);
    }

    // ─── Storefront API query ──────────────────────────────────────────────────
    function buildQuery(productId) {
        return JSON.stringify({
            query: `{
        productRecommendations(productId: "gid://shopify/Product/${productId}", intent: COMPLEMENTARY) {
          id
          title
          handle
          availableForSale
          featuredImage {
            url
            altText
          }
          variants(first: 1) {
            edges {
              node {
                id
                availableForSale
                price { amount }
              }
            }
          }
        }
      }`
        });
    }

    // ─── Session cache helpers ─────────────────────────────────────────────────
    function cacheGet(productId) {
        try {
            const raw = sessionStorage.getItem(CACHE_PREFIX + productId);
            if (!raw) return null;
            const { data, ts } = JSON.parse(raw);
            if (Date.now() - ts > 30 * 60 * 1000) {
                sessionStorage.removeItem(CACHE_PREFIX + productId);
                return null;
            }
            return data;
        } catch (_) {
            return null;
        }
    }

    function cacheSet(productId, data) {
        try {
            sessionStorage.setItem(
                CACHE_PREFIX + productId,
                JSON.stringify({ data, ts: Date.now() })
            );
        } catch (_) { }
    }

    // ─── Build a product row element ───────────────────────────────────────────
    function buildItemHTML(product, variantGid, price, imageUrl, imageAlt) {
        const variantId = variantGid.replace('gid://shopify/ProductVariant/', '');
        const handle = product.handle;
        const title = product.title;
        const money = parseFloat(price).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
        });

        return `
      <div class="cart-fbt--item">
        <a class="cart-fbt--image-link" href="/products/${handle}">
          <img
            src="${imageUrl || ''}"
            alt="${imageAlt || title}"
            width="50"
            height="50"
            loading="lazy"
          >
        </a>
        <div class="cart-fbt--info">
          <a class="cart-fbt--title" href="/products/${handle}">
            ${title}
          </a>
          <div class="cart-fbt--price money">${money}</div>
        </div>
        <button
          class="cart-fbt--add"
          data-item="button"
          data-fbt-add
          data-variant-id="${variantId}"
          aria-label="Add ${title} to cart"
        >
          Add
        </button>
      </div>
    `;
    }

    // ─── Render suggestions into the container ─────────────────────────────────
    function renderSuggestions(container, products, cartProductIds) {
        const filtered = products.filter(p => {
            const numericId = p.id.replace('gid://shopify/Product/', '');
            return p.availableForSale && !cartProductIds.some(id => id.trim() === numericId.trim());
        });

        if (filtered.length === 0) {
            const root = container.closest('.cart-fbt--root');
            if (root) root.style.display = 'none';
            return;
        }

        const html = filtered
            .slice(0, MAX_SUGGESTIONS)
            .map(p => {
                const variant = p.variants.edges[0]?.node;
                if (!variant || !variant.availableForSale) return '';
                return buildItemHTML(
                    p,
                    variant.id,
                    variant.price.amount,
                    p.featuredImage?.url,
                    p.featuredImage?.altText
                );
            })
            .join('');

        container.innerHTML = html;
    }

    // ─── Fetch from Storefront API ─────────────────────────────────────────────
    async function fetchSuggestions(productId) {
        const cached = cacheGet(productId);
        if (cached) return cached;

        const response = await fetch(STOREFRONT_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
            },
            body: buildQuery(productId)
        });

        if (!response.ok) throw new Error('Storefront API error: ' + response.status);

        const json = await response.json();
        const products = json?.data?.productRecommendations || [];
        cacheSet(productId, products);
        return products;
    }

    // ─── Initialize API-mode containers ───────────────────────────────────────
    function initFbtContainers() {
        const containers = document.querySelectorAll('[data-fbt-api]');
        if (!containers.length) return;

        const uninitializedContainers = [...containers].filter(c => !c.dataset.fbtLoaded);
        if (!uninitializedContainers.length) return;

        fetch('/cart.js')
            .then(r => r.json())
            .then(cart => {
                if (!cart.item_count) return;

                const productId = cart.items[0].product_id.toString();
                const cartIds = cart.items.map(i => i.product_id.toString());

                uninitializedContainers.forEach(async (container) => {
                    container.dataset.fbtLoaded = 'true';
                    try {
                        const products = await fetchSuggestions(productId);
                        renderSuggestions(container, products, cartIds);
                    } catch (err) {
                        const root = container.closest('.cart-fbt--root');
                        if (root) root.style.display = 'none';
                        console.warn('[Pingree FBT] Could not load suggestions:', err.message);
                    }
                });
            })
            .catch(() => {
                uninitializedContainers.forEach(container => {
                    const root = container.closest('.cart-fbt--root');
                    if (root) root.style.display = 'none';
                });
            });
    }

    // ─── Add to cart handler ───────────────────────────────────────────────────
    document.addEventListener('click', function (e) {
        const btn = e.target.closest('[data-fbt-add]');
        if (!btn) return;
        if (btn.dataset.fbtProcessing) return;
        btn.dataset.fbtProcessing = 'true';

        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        btn.disabled = true;
        btn.textContent = '...';

        fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
        })
            .then(() => {
                btn.textContent = 'Added';
                setTimeout(() => {
                    // Reload the page with ?cart=open so the drawer auto-opens
                    const url = new URL(window.location.href);
                    url.searchParams.set('cart', 'open');
                    window.location.href = url.toString();
                }, 800);
            })
            .catch(() => {
                btn.textContent = 'Error';
                btn.disabled = false;
                delete btn.dataset.fbtProcessing;
            });
    });

    // ─── Watch for cart drawer opens and cart updates ──────────────────────────
    window.addEventListener('theme:cart:updated', initFbtContainers);
    window.addEventListener('theme:drawer:right:opened', initFbtContainers);
    document.addEventListener('DOMContentLoaded', function () {
        maybeOpenDrawer();
        initFbtContainers();
    });

})();